import React, {useMemo} from 'react';
import TablePage from "../../pages/tablePage/TablePage";
import {useNavigate} from "react-router-dom";
import {Button, Typography, Switch, Popconfirm} from "antd";
import toast from "react-hot-toast";
import {
    useDeleteNewProbabilityData,
    useNewProbabilityData,
    useNewUploadProbabilityData,
    useTestUploadProbabilityData,
    useSaveNewProbabilityOnOff
} from "./useProbabilityData";
import {useCountryData} from "../country/useCountryData";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";

const {Text} = Typography;
const PROBABILITY_URL = 'https://production.repos.kf.actgames.co/page/probability/index.html?lang=ko';
const TEST_PROBABILITY_URL = 'https://production.repos.kf.actgames.co/page/probability/index_t.html?lang=ko';

const reorderKeys = (data) => {
    const reorderedData = {};
    let index = 0;

    Object.entries(data).forEach(([key, value]) => {
        reorderedData[index] = value; // Assign new sequential keys
        index++;
    });

    return reorderedData;
};

const NewProbability = () => {
    const { data: countryData, isLoading: isCountryLoading, error: countryError } = useCountryData();
    const { mutate: uploadProbability, isPending: isUploading } = useNewUploadProbabilityData();
    const { mutate: testUploadProbability, isPending: isTestUploading } = useTestUploadProbabilityData();
    const { data: probabilityData, isLoading: isProbLoading, error: probError } = useNewProbabilityData();
    const { mutate: deleteProbability, isPending: isDeleting } = useDeleteNewProbabilityData();
    const { mutate: saveOnOff } = useSaveNewProbabilityOnOff();
    const navigate = useNavigate();

    const handleUpload = () => {
        uploadProbability();
    };

    const handleTestUpload = () => {
        testUploadProbability();
    };

    const localData = useMemo(() => {
        if (!probabilityData) return [];
        return probabilityData.map(probability => ({
            seq: probability.seq,
            onOff: probability.onOff,
            ...probability.data
        }))
    }, [probabilityData]);

    const handleDelete = (keyToDelete) => {
        console.log(keyToDelete);
        deleteProbability(keyToDelete);
        // updateProbability(reorderedData);
    };

    const handleToggleOnOff = (item) => {
        console.log(item)
        saveOnOff({
            seq: item.seq,
            onOff: item.onOff ? 0 : 1
        });
    };

    const columns = useMemo(() => {
        const defaultColumns = [{
            title: '시퀀스',
            dataIndex: 'seq',
            key: 'seq',
            width: 80,
            fixed: 'left',
        }];
        const deleteColumn = [{
            title: 'Action',
            render: (_, { seq }) => (
                <div>
                    <Button
                        type="primary"
                        variant={'outlined'}
                        color={'blue'}
                        onClick={() => {
                            const queryParams = new URLSearchParams({
                                index: seq
                            }).toString();
                            navigate(`/itemProbability/editor?${queryParams}`);
                        }}
                        style={{marginRight: '8px'}}
                    >
                        수정
                    </Button>
                    <Button
                        danger
                        onClick={() => handleDelete(seq)} disabled={isDeleting}
                    >
                        {isDeleting ? '삭제 중...' : '삭제'}
                    </Button>
                </div>
            )
        }];
        const onOffColumn = [{
            title: 'On/Off',
            render: (_, { seq }) => {
                const item = probabilityData.find(e => e.seq === seq);
                return (
                    <Switch
                        checked={!!item?.onOff}
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                        onChange={() => handleToggleOnOff(item)}
                        style={{
                            backgroundColor: item?.onOff ? '#52c41a' : '#bfbfbf',
                            border: '1px solid #d9d9d9',
                            minWidth: 60
                        }}
                    />
                );
            }
        }]

        if (!countryData || !probabilityData) return [];

        const uniqueLangCode = [...new Set(countryData.map(item => item.langCode))];
        const langColumns = uniqueLangCode.map((langCode) => ({
            title: countryData.find(e => e.langCode === langCode).languageName,
            render: (data) => {
                return data[langCode]?.title || ''
            },
        }));
        return [...defaultColumns, ...langColumns, ...deleteColumn, ...onOffColumn];
    }, [countryData, probabilityData, isDeleting, navigate, handleDelete]);

    const handleClick = () => {
        window.open(PROBABILITY_URL, '_blank');
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(PROBABILITY_URL).then(() => {
            toast.success(`복사되었습니다.`);
        }).catch(() => {
            toast.error('복사 실패.');
        });
    };

    const props = {
        columns,
        localData
    }

    if (isProbLoading || isCountryLoading) return <IsLoading />;
    const firstError = probError || countryError;
    if (firstError) return <ErrorPage error={firstError} />;

    const handleTestClick = () => {
        window.open(TEST_PROBABILITY_URL, '_blank');
    };

    const handleTestCopy = () => {
        navigator.clipboard.writeText('https://production.repos.kf.actgames.co/page/probability/index_t.html').then(() => {
            toast.success(`복사되었습니다.`);
        }).catch(() => {
            toast.error('복사 실패.');
        });
    };

    return (
        <>
            {/* 라이브 영역 */}
            <div style={{marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div>
                    <Button type="link" onClick={handleClick}>
                        페이지 바로가기
                    </Button>
                    <Text type="secondary" style={{cursor: 'pointer'}} onClick={handleCopy}>
                        URL: https://production.repos.kf.actgames.co/page/probability/index.html
                    </Text>
                </div>
                <Popconfirm
                    title="업로드 확인"
                    description="업로드하면 라이브서버에 즉시 적용됩니다."
                    onConfirm={handleUpload}
                    okText="확인"
                    cancelText="취소"
                    okButtonProps={{ danger: true }}
                >
                    <Button
                        type="primary"
                        style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
                        loading={isUploading}
                    >
                        {isUploading ? '업로드 중...' : '업로드 하기'}
                    </Button>
                </Popconfirm>
            </div>
            {/* 테스트 영역 */}
            <div style={{marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div>
                    <Button type="link" onClick={handleTestClick}>
                        테스트 페이지 바로가기
                    </Button>
                    <Text type="secondary" style={{cursor: 'pointer'}} onClick={handleTestCopy}>
                        URL: https://production.repos.kf.actgames.co/page/probability/index_t.html
                    </Text>
                </div>
                <Button
                    type="primary"
                    style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                    loading={isTestUploading}
                    onClick={handleTestUpload}
                >
                    {isTestUploading ? '테스트 업로드 중...' : '테스트 업로드'}
                </Button>
            </div>
            <TablePage {...props}/>
        </>
    );
};

export default NewProbability;